/**
 * src/modules/legacy-utils.ts — Sprint 20.2 Etap 1 ekstrakcji app.js (2026-05-02).
 *
 * Pure utility functions wyciągnięte z app.js:
 *   - downloadBlob (DOM helper)
 *   - _fmtMoneyPL (PLN format)
 *   - parseDeliveryAddress (address normalize)
 *   - normalizePaymentStatusFromBackend (status mapping)
 *   - normalizePaymentMethodLabel (PL labels)
 *   - normalizeStatusLabel (PL order status mapping)
 *   - sourceMetaFromRaw (allegro/manual/woo metadata)
 *
 * Wszystkie eksposed jako window.X dla compat z app.js delegates.
 */

/** Download Blob jako plik (createObjectURL + click + revoke). */
export function downloadBlob(blob: Blob, fileName?: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName || 'download';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/** Format liczby jako PLN locale (2 miejsca po przecinku domyślnie). */
export function _fmtMoneyPL(n: any, decimals?: number): string {
  const d = decimals == null ? 2 : decimals;
  return (Number(n) || 0).toLocaleString('pl-PL', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

/** Adres dostawy z różnych formatów źródłowych → unified shape. */
export function parseDeliveryAddress(addr: any): {
  street: string; city: string; postal_code: string; country: string;
} {
  addr = addr || {};
  let street = '';
  if (addr.street && addr.houseNumber) {
    street = String(addr.street) + ' ' + String(addr.houseNumber);
  } else {
    street = addr.street || addr.address_line1 || '';
  }
  return {
    street,
    city: addr.city || '',
    postal_code: addr.postal_code || addr.zipCode || addr.postcode || '',
    // 2026-06-29 FIX: Allegro podaje `countryCode` (SK/CZ/...), nie `country` — bez tego
    // zagraniczne adresy pokazywały domyślną "Polska".
    country: addr.country || addr.countryCode || addr.country_code || 'Polska',
  };
}

/** Normalizuj raw payment status z backendu (Allegro/PrestaShop/manual) → unified token. */
export function normalizePaymentStatusFromBackend(
  rawStatus: any,
  amount: any,
  paidAmount: any,
  refundedAmount: any,
): 'paid' | 'partially_paid' | 'pending' | 'refunded' | 'partially_refunded' {
  const txt = String(rawStatus || '').trim().toLowerCase();
  let orderAmount = parseFloat(amount || 0);
  if (!isFinite(orderAmount) || orderAmount < 0) orderAmount = 0;
  let paid = parseFloat(paidAmount || 0);
  if (!isFinite(paid) || paid < 0) paid = 0;
  let refunded = parseFloat(refundedAmount || 0);
  if (!isFinite(refunded) || refunded < 0) refunded = 0;
  if (refunded > 0) {
    if (orderAmount > 0 && refunded >= (orderAmount - 0.01)) return 'refunded';
    return 'partially_refunded';
  }
  if (!txt) {
    if (orderAmount > 0 && paid > 0 && paid < orderAmount) return 'partially_paid';
    if (orderAmount > 0 && paid >= orderAmount) return 'paid';
    return 'pending';
  }
  if (txt.indexOf('refund') >= 0 || txt.indexOf('zwrot') >= 0 || txt.indexOf('chargeback') >= 0) return 'refunded';
  if (txt.indexOf('partially_refunded') >= 0) return 'partially_refunded';
  if (txt.indexOf('partial') >= 0 || txt.indexOf('części') >= 0 || txt.indexOf('czesci') >= 0) {
    if (orderAmount > 0 && paid >= orderAmount) return 'paid';
    return 'partially_paid';
  }
  if (
    txt === 'paid' ||
    txt.indexOf('paid') >= 0 ||
    txt.indexOf('complete') >= 0 ||
    txt.indexOf('finish') >= 0 ||
    txt.indexOf('success') >= 0 ||
    txt.indexOf('settled') >= 0 ||
    txt === 'opłacone' ||
    txt === 'oplacone'
  ) {
    if (orderAmount > 0 && paid > 0 && paid < orderAmount) return 'partially_paid';
    return 'paid';
  }
  if (
    txt.indexOf('pending') >= 0 ||
    txt.indexOf('start') >= 0 ||
    txt.indexOf('wait') >= 0 ||
    txt.indexOf('not_paid') >= 0 ||
    txt.indexOf('unpaid') >= 0 ||
    txt.indexOf('new') >= 0 ||
    txt.indexOf('processing') >= 0 ||
    txt.indexOf('in_progress') >= 0 ||
    txt.indexOf('cancel') >= 0
  ) {
    if (orderAmount > 0 && paid > 0 && paid < orderAmount) return 'partially_paid';
    if (orderAmount > 0 && paid >= orderAmount) return 'paid';
    return 'pending';
  }
  if (orderAmount > 0 && paid > 0 && paid < orderAmount) return 'partially_paid';
  if (orderAmount > 0 && paid >= orderAmount) return 'paid';
  return 'pending';
}

/** Mapowanie raw payment method → PL label. */
export function normalizePaymentMethodLabel(rawMethod: any): string {
  const method = String(rawMethod || '').trim();
  if (!method) return '';
  const key = method.toUpperCase();
  const map: Record<string, string> = {
    'CASH_ON_DELIVERY': 'Za pobraniem',
    'CARD_ON_DELIVERY': 'Karta przy odbiorze',
    'ONLINE': 'Przelew online',
    'TRANSFER': 'Przelew',
    'BANK_TRANSFER': 'Przelew bankowy',
    'ALLEGRO_PAY': 'Allegro Pay',
    'ALLEGRO_FINANCE': 'Allegro Pay',
  };
  if (map[key]) return map[key];
  return method;
}

/**
 * Surowy status (różne źródła) → nazwa do pokazania.
 *
 * ETAP 3 (2026-08-10): nazwa pochodzi TERAZ Z DRZEWKA FIRMY, a nie z zakutej mapy
 * poniżej. Bez tego zmiana nazwy statusu była niewidoczna: user przemianowywał
 * „Spakowane" na „Zapakowane", zapisywał — i lista dalej pokazywała „Spakowane",
 * bo `map` niżej odwzorowywała `packed` z powrotem na starą nazwę.
 *
 * Mapa poniżej ZOSTAJE jako zapas: pierwszy render listy potrafi wyprzedzić
 * odpowiedź `/api/order-status-tree`, a wtedy lepiej pokazać „Spakowane" niż
 * surowe `packed`.
 */
export function normalizeStatusLabel(statusName: any): string {
  const txt = String(statusName || '').trim();
  if (!txt) return 'Nowe';
  try {
    const w = window as any;
    const zDrzewka = w.__sbExports && typeof w.__sbExports.nazwaDlaKlucza === 'function'
      ? String(w.__sbExports.nazwaDlaKlucza(w.__sbExports.kluczStatusu(txt)) || '')
      : '';
    if (zDrzewka) return zDrzewka;
  } catch (_e) { /* drzewko jeszcze niegotowe — zapasowa mapa niżej */ }
  let key = txt.toLowerCase();
  try {
    key = key.normalize('NFD').replace(/[̀-ͯ]/g, '');
  } catch (_e) { /* ignore */ }
  key = key.replace(/ł/g, 'l').replace(/[^a-z0-9]/g, '');
  const map: Record<string, string> = {
    nowe: 'Nowe',
    new: 'Nowe',
    bought: 'Nowe',
    potwierdzone: 'Potwierdzone',
    confirmed: 'Potwierdzone',
    wrealizacji: 'W realizacji',
    processing: 'W realizacji',
    filledin: 'W realizacji',
    readyforprocessing: 'W realizacji',
    spakowane: 'Spakowane',
    packed: 'Spakowane',
    // 2026-06-05: "Do wysłania"/ready_to_ship MUSI być przed zachłannym `indexOf('wyslan')`
    // niżej — inaczej klucz "dowyslania" łapie się jako "Wysłane" (status przekłamany,
    // filtr "Do wysłania" nigdy nie trafia → lista pusta mimo licznika).
    dowyslania: 'Do wysłania',
    readytoship: 'Do wysłania',
    wyslane: 'Wysłane',
    wysane: 'Wysłane',
    wysaane: 'Wysłane',
    shipped: 'Wysłane',
    sent: 'Wysłane',
    dostarczone: 'Dostarczone',
    delivered: 'Dostarczone',
    // F2 rundy statusowej 2026-07-19: klucze kanoniczne, których brakowało —
    // bez nich 'picked_up'/'ready_for_pickup' renderowały się po angielsku.
    odebrane: 'Odebrane',
    pickedup: 'Odebrane',
    gotowedoodbioru: 'Gotowe do odbioru',
    readyforpickup: 'Gotowe do odbioru',
    doodbioru: 'Gotowe do odbioru',
    anulowane: 'Anulowane',
    cancelled: 'Anulowane',
    canceled: 'Anulowane',
    zwrot: 'Zwrot',
    returned: 'Zwrot',
  };
  if (map[key]) return map[key];
  if (key.indexOf('wyslan') >= 0) return 'Wysłane';
  if (key.indexOf('dostarcz') === 0) return 'Dostarczone';
  if (key.indexOf('potwierdz') === 0) return 'Potwierdzone';
  if (key.indexOf('anul') === 0) return 'Anulowane';
  if (key.indexOf('zwrot') === 0) return 'Zwrot';
  return txt;
}

/** Source name → display metadata (label + color CSS class). */
export function sourceMetaFromRaw(rawSource: any): { key: string; label: string; color: string } {
  const s = String(rawSource || 'allegro').toLowerCase();
  if (s === 'manual') return { key: 'manual', label: 'Ręczne', color: 'source-manual' };
  if (s === 'woocommerce') return { key: 'woocommerce', label: 'WooCommerce', color: 'source-woo' };
  return { key: 'allegro', label: 'Allegro', color: 'source-allegro' };
}

// Window expose dla legacy app.js delegates (Sprint 20.2 Etap 1)
if (typeof window !== 'undefined') {
  const w = window as any;
  // Tylko jeśli jeszcze nie ustawione przez app.js (let app.js win — gradual migration)
  if (typeof w.downloadBlob !== 'function') w.downloadBlob = downloadBlob;
  if (typeof w._fmtMoneyPL !== 'function') w._fmtMoneyPL = _fmtMoneyPL;
  if (typeof w.parseDeliveryAddress !== 'function') w.parseDeliveryAddress = parseDeliveryAddress;
  if (typeof w.normalizePaymentStatusFromBackend !== 'function') w.normalizePaymentStatusFromBackend = normalizePaymentStatusFromBackend;
  if (typeof w.normalizePaymentMethodLabel !== 'function') w.normalizePaymentMethodLabel = normalizePaymentMethodLabel;
  if (typeof w.normalizeStatusLabel !== 'function') w.normalizeStatusLabel = normalizeStatusLabel;
  if (typeof w.sourceMetaFromRaw !== 'function') w.sourceMetaFromRaw = sourceMetaFromRaw;
}
